import { Modal, ModalHeader, ModalBody, Spinner } from 'reactstrap';
import { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Context as AuthContext } from '../../../../../contexts/AuthContext';
import PerformanceVerbalWarningService from '../../../../../services/PerformanceVerbalWarning';
import VerbalWarningCard from './VerbalWarningCard';
import SummaryModalRecordRow from '../components/SummaryModalRecordRow';

interface VerbalWarningItem {
  id?: number;
  name?: string;
  date?: string;
  dateOfIncident?: string;
  position?: string;
  department?: string;
  supervisor?: string;
  date_of_hire?: string;
  dateOfHire?: string;
  policy_violation?: string;
  policyViolation?: { name: string };
  policyViolations?: { name?: string }[];
  policy_violated?: string;
  reason_for_verbal_warning?: string;
  reasonForVerbalWarning?: string;
  next_consequences?: string;
  nextConsequences?: string;
  expectation_of_improvement?: string;
  expectationOfImprovement?: string;
  private_notes?: string;
  privateNotes?: string;
  created_at?: string;
  createdAt?: string;
}

interface VerbalWarningSummaryModalProps {
  isOpen: boolean;
  toggle: () => void;
  userId?: string | null;
  userName?: string;
  verbalWarningData?: VerbalWarningItem[];
}

const getVerbalWarningListDescription = (item: VerbalWarningItem) => {
  if (item.policyViolations && Array.isArray(item.policyViolations)) {
    const joined = item.policyViolations.map((pv: any) => pv.name || pv).join(', ');
    if (joined) {
      return joined;
    }
  }
  return item.policy_violation ?? item.policyViolation?.name ?? item.policy_violated ?? '';
};

const VerbalWarningSummaryModal = ({
  isOpen,
  toggle,
  userId,
  userName,
  verbalWarningData,
}: VerbalWarningSummaryModalProps) => {
  const [verbalWarnings, setVerbalWarnings] = useState<VerbalWarningItem[]>([]);
  const [selected, setSelected] = useState<VerbalWarningItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { token } = useContext(AuthContext);

  const formatDate = (date: string | undefined) => {
    if (!date) return '-';
    return moment.utc(date).format('MM/DD/YYYY');
  };

  const loadVerbalWarnings = useCallback(async () => {
    if (verbalWarningData) {
      setVerbalWarnings(verbalWarningData);
      return;
    }

    if (!userId || !token) {
      return;
    }

    setIsLoading(true);
    try {
      const service = PerformanceVerbalWarningService(token);
      const response = await service.getUserVerbalWarnings(userId, 'verbal-warning');
      if (response?.data) {
        setVerbalWarnings(response.data);
      }
    } catch (error: any) {
      console.error('Error loading verbal warnings:', error);
      toast.error(error?.response?.data?.message || 'Error loading verbal warnings');
    } finally {
      setIsLoading(false);
    }
  }, [userId, token, verbalWarningData]);

  useEffect(() => {
    if (isOpen) {
      setSelected(null);
      loadVerbalWarnings();
    }
  }, [isOpen, loadVerbalWarnings]);

  const handleClose = () => {
    setSelected(null);
    setVerbalWarnings([]);
    toggle();
  };

  const renderDetailCard = (verbalWarning: VerbalWarningItem, index: number) => (
    <VerbalWarningCard
      key={verbalWarning.id || index}
      index={index}
      name={verbalWarning.name ?? ''}
      date={formatDate(verbalWarning.date || verbalWarning.dateOfIncident)}
      position={verbalWarning.position ?? ''}
      department={verbalWarning.department ?? ''}
      supervisor={verbalWarning.supervisor ?? ''}
      dateOfHire={formatDate(verbalWarning.date_of_hire ?? verbalWarning.dateOfHire)}
      policyViolation={
        verbalWarning.policyViolations && Array.isArray(verbalWarning.policyViolations)
          ? verbalWarning.policyViolations.map((pv: any) => pv.name || pv).join(', ')
          : verbalWarning.policy_violation ?? verbalWarning.policyViolation?.name ?? 'N/A'
      }
      reasonForVerbalWarning={verbalWarning.reason_for_verbal_warning ?? verbalWarning.reasonForVerbalWarning ?? ''}
      nextConsequences={verbalWarning.next_consequences ?? verbalWarning.nextConsequences ?? ''}
      expectationOfImprovement={
        verbalWarning.expectation_of_improvement ?? verbalWarning.expectationOfImprovement ?? ''
      }
      privateNotes={verbalWarning.private_notes ?? verbalWarning.privateNotes}
      createdAt={formatDate(verbalWarning.created_at ?? verbalWarning.createdAt)}
    />
  );

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      {selected ? (
        <div className="modal-header align-items-start">
          <button
            type="button"
            className="btn btn-link p-0 mr-3 text-dark mt-1"
            onClick={() => setSelected(null)}
            aria-label="Back"
          >
            <i className="fas fa-arrow-left fa-lg" />
          </button>
          <div className="flex-grow-1">
            <h5 className="mb-0 modal-title">Verbal Warning</h5>
            {userName ? <div className="text-muted small">{userName}</div> : null}
          </div>
          <button type="button" className="close" onClick={handleClose} aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      ) : (
        <ModalHeader toggle={handleClose} tag="div">
          <div>
            <h5 className="mb-0 modal-title">Verbal Warning</h5>
            {userName ? <div className="text-muted small">{userName}</div> : null}
          </div>
        </ModalHeader>
      )}
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto', paddingTop: selected ? '1rem' : '0' }}>
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : selected ? (
          <div className="p-1">{renderDetailCard(selected, 0)}</div>
        ) : (
          <>
            {verbalWarnings.length === 0 ? (
              <div className="text-center text-muted py-4">
                <i className="fas fa-inbox fa-3x mb-3" />
                <p>No verbal warning records found</p>
              </div>
            ) : (
              <div className="pt-2">
                {verbalWarnings.map((verbalWarning, index) => (
                  <SummaryModalRecordRow
                    key={verbalWarning.id || index}
                    dateText={formatDate(verbalWarning.date || verbalWarning.dateOfIncident)}
                    description={getVerbalWarningListDescription(verbalWarning) || '—'}
                    onClick={() => setSelected(verbalWarning)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default VerbalWarningSummaryModal;
